import {SVG} from '../../../../../constants/assetLists';
import {useMemo} from 'react';
import AppTable, {ColumnDef} from '../../../../../components/common/AppTable';
import {useGroupsContext} from '../../../../../context/users/GroupsContextProvider';
import AppTooltip from '../../../../../components/common/AppTooltip';
import {GroupItem} from '../groups';
import {ICONS} from '../../../data';
import {highlightSearchMatch} from '../../../../operations/components/AnnouncementsAndTasks/components/AnnouncementCard';
import {getRoleTypeName} from '../../../../../utils';

function GroupsTable() {
  const {
    allUserGroupsData,
    selectedGroups,
    setSelectedGroups,
    searchText,
    page,
    setPage,
  } = useGroupsContext();

  const columns = useMemo<ColumnDef<GroupItem>[]>(
    () => [
      {
        title: 'קבוצה',
        field: 'groupName',
        accessor: r => r?.groupName,
        sortable: true,
        render: r => {
          const groupIcon = ICONS.find(icon => icon.id === r?.icon)?.icon || '';

          return (
            <AppTooltip
              title={
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <p className="m-0 font-medium text-[16px]">
                      {r?.groupName}
                    </p>

                    <p className="m-0 text-[16px] font-normal border-b border-b-tealBlue">
                      {r?.description}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    {r?.associatedUsers?.map((user: any, index: number) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-[16px] font-normal">
                          {user?.firstName} {user.lastName}
                        </span>

                        <div className="w-[1px] h-[16px] rounded-[3px] bg-black" />

                        <span className="text-[16px] font-normal">
                          {getRoleTypeName(user?.roles?.[0]?.roleType) || ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              }>
              <div className="flex items-center gap-2">
                {groupIcon && (
                  <img
                    src={groupIcon}
                    alt="icon"
                    className="w-[20px] h-[20px] object-contain"
                  />
                )}

                <span>
                  {highlightSearchMatch(r?.groupName || '', searchText)}
                </span>
              </div>
            </AppTooltip>
          );
        },
      },
      {
        title: 'תיאור',
        field: 'description',
        accessor: 'description',
        searchable: false,
        sortable: true,
        cellClassName: 'max-w-[300px]',
      },
      {
        title: 'צבע',
        field: 'color',
        accessor: 'color',
        sortable: false,
        searchable: false,
        render: r => (
          <div
            className="w-[16px] h-[16px] rounded-[3px] border-1 border-[#707070]"
            style={{backgroundColor: r.color}}
          />
        ),
      },
      {
        title: '# משתמשים משוייכים',
        field: 'noOfAssigns',
        accessor: 'numberOfAssociatedUsers',
        sortable: true,
        searchable: false,
      },
      {
        title: 'סטאטוס חשבון',
        field: 'isActive',
        accessor: 'isActive',
        searchable: false,
        sortable: true,
        render: r => (
          <div className="flex items-center gap-2">
            {r?.isActive ? (
              <div className="w-[8px] h-[8px] rounded-full bg-green" />
            ) : (
              <img
                src={SVG.closeGrayIcon}
                alt="icon"
                className="w-[10px] h-[10px] object-contain"
              />
            )}
            <span>{r?.isActive ? 'פעיל' : 'לא פעיל'}</span>
          </div>
        ),
      },
      {
        title: 'בית עסק',
        field: 'client',
        searchable: false,
        accessor: r => r?.client?.name,
        sortable: true,
      },
    ],
    [searchText],
  );

  return (
    <AppTable<GroupItem>
      data={allUserGroupsData?.data || []}
      columns={columns}
      rowKey={r => r?.id}
      selectable
      searchText={searchText}
      selectedKeys={selectedGroups}
      onSelectionChange={setSelectedGroups}
      rowDisabled={r => !r.isActive}
      currentPage={page - 1}
      limit={50}
      onPageChange={page => setPage(page + 1)}
      totalRecords={allUserGroupsData?.pagination?.total}
      showPagination={(allUserGroupsData?.pagination?.total || 0) > 50}
    />
  );
}

export default GroupsTable;
